import { MultiSelectField } from '@components/molecules/MultiSelectField'
import { SelectField, type SelectFieldOption } from '@components/molecules/SelectField'
import { SegmentedControl, type SegmentedOption } from '@components/atoms/SegmentedControl'

import { scopedTranslation } from '@utils/I18n'

import { type LayoutMode } from '../roster_board/layoutMode'

import { type CoverageFilter, type RosterFilters, type StatusFilter, type SupporterOption } from './types'

const t = scopedTranslation('roster_board')

type RosterFilterToolbarProps = {
  filters: RosterFilters
  roleOptions: string[]
  peopleOptions: SupporterOption[]
  onRoles: (roles: string[]) => void
  onPeople: (people: string[]) => void
  onStatus: (status: StatusFilter) => void
  onCoverage: (coverage: CoverageFilter) => void
  layout: LayoutMode
  onLayoutChange: (layout: LayoutMode) => void
  showLayoutToggle: boolean
}

export function RosterFilterToolbar({
  filters,
  roleOptions,
  peopleOptions,
  onRoles,
  onPeople,
  onStatus,
  onCoverage,
  layout,
  onLayoutChange,
  showLayoutToggle,
}: RosterFilterToolbarProps) {
  const statusOptions: SelectFieldOption<StatusFilter>[] = [
    { value: 'all', label: t('filters.status.any') },
    { value: 'published', label: t('filters.status.published') },
    { value: 'draft', label: t('filters.status.draft') },
  ]

  const coverageOptions: SelectFieldOption<CoverageFilter>[] = [
    { value: 'any', label: t('filters.coverage.any') },
    { value: 'needs', label: t('filters.coverage.needs') },
    { value: 'full', label: t('filters.coverage.full') },
  ]

  const layoutOptions: SegmentedOption<LayoutMode>[] = [
    { value: 'list', label: t('layout_list'), leadingIcon: 'dot-points' },
    { value: 'cards', label: t('layout_cards'), leadingIcon: 'copy-06' },
  ]

  return (
    <div className="tw:flex tw:flex-wrap tw:items-end tw:gap-3">
      <div className="tw:w-full tw:sm:w-52">
        <MultiSelectField
          name="role-filter"
          label={t('filters.role_label')}
          placeholder={t('filters.role.any')}
          options={roleOptions.map((role) => ({ value: role, label: role }))}
          value={filters.roles}
          onValueChange={onRoles}
          showSelectedPills={false}
          searchPlaceholder={t('filters.search_placeholder')}
          noResultsText={t('filters.no_matches')}
        />
      </div>
      <div className="tw:w-full tw:sm:w-48">
        <MultiSelectField
          name="people-filter"
          label={t('filters.people_label')}
          placeholder={t('filters.people.any')}
          options={peopleOptions.map((option) => ({ value: option.id, label: option.fullName }))}
          value={filters.people}
          onValueChange={onPeople}
          showSelectedPills={false}
          searchPlaceholder={t('filters.search_placeholder')}
          noResultsText={t('filters.no_matches')}
        />
      </div>
      <div className="tw:w-full tw:sm:w-44">
        <SelectField
          name="status-filter"
          label={t('filters.status_label')}
          options={statusOptions}
          value={filters.status}
          onValueChange={onStatus}
        />
      </div>
      <div className="tw:w-full tw:sm:w-48">
        <SelectField
          name="coverage-filter"
          label={t('filters.coverage_label')}
          options={coverageOptions}
          value={filters.coverage}
          onValueChange={onCoverage}
        />
      </div>

      {showLayoutToggle && (
        <SegmentedControl
          className="tw:ml-auto"
          options={layoutOptions}
          value={layout}
          onChange={onLayoutChange}
          ariaLabel={t('layout_group_label')}
        />
      )}
    </div>
  )
}
